import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useTransform, animate } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
// A numeric counter that eases UP to each new target value (instead of
// snapping/dropping in) so it reads like a live, climbing tally.
function RollingCounter({
  value
}: {
  value: number;
}) {
  const reduceMotion = useReducedMotion();
  const mv = useMotionValue(value);
  const rounded = useTransform(mv, v => Math.round(v).toLocaleString());
  const [display, setDisplay] = useState(value.toLocaleString());
  const prev = useRef(value);
  useEffect(() => {
    const unsub = rounded.on('change', v => setDisplay(v));
    return () => unsub();
  }, [rounded]);
  useEffect(() => {
    if (reduceMotion) {
      mv.set(value);
      return;
    }
    const controls = animate(mv, value, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1]
    });
    prev.current = value;
    return () => controls.stop();
  }, [value, reduceMotion, mv]);
  return <span className="tabular-nums" data-id="element-5288" data-mp="22he4iuqs62ku">{display}</span>;
}
// Every tool slug below is a vetted, full-color brand icon that exists on the
// dashboard-icons CDN — so no tile ever renders blank or invisible.
const TASKS = [{
  id: 1,
  text: 'Drafting Q3 update',
  tool: 'notion'
}, {
  id: 2,
  text: 'Updating 14 stale deals',
  tool: 'salesforce'
}, {
  id: 3,
  text: 'Triaging Sentry errors',
  tool: 'linear'
}, {
  id: 4,
  text: 'Pausing low ROAS ads',
  tool: 'mailchimp'
}, {
  id: 5,
  text: 'Reconciling payouts',
  tool: 'stripe'
}, {
  id: 6,
  text: 'Summarizing feedback',
  tool: 'zendesk'
}, {
  id: 7,
  text: 'Merging dependabot PRs',
  tool: 'github'
}, {
  id: 8,
  text: 'Syncing lead scores',
  tool: 'hubspot'
}, {
  id: 9,
  text: 'Generating weekly report',
  tool: 'slack'
}, {
  id: 10,
  text: 'Closing resolved tickets',
  tool: 'jira'
}, {
  id: 11,
  text: 'Exporting design assets',
  tool: 'figma'
}, {
  id: 12,
  text: 'Sending payment reminders',
  tool: 'quickbooks'
}, {
  id: 13,
  text: 'Categorizing feature requests',
  tool: 'notion'
}, {
  id: 14,
  text: 'Routing inbound leads',
  tool: 'salesforce'
}, {
  id: 15,
  text: 'Approving expense reports',
  tool: 'stripe'
}, {
  id: 16,
  text: 'Updating sprint board',
  tool: 'linear'
}, {
  id: 17,
  text: 'Drafting release notes',
  tool: 'notion'
}, {
  id: 18,
  text: 'Analyzing churn risk',
  tool: 'salesforce'
}, {
  id: 19,
  text: 'Scheduling candidate interviews',
  tool: 'asana'
}, {
  id: 20,
  text: 'Reviewing ad copy',
  tool: 'mailchimp'
}, {
  id: 21,
  text: 'Deploying hotfix',
  tool: 'github'
}, {
  id: 22,
  text: 'Formatting blog post',
  tool: 'wordpress'
}, {
  id: 23,
  text: 'Scraping competitor pricing',
  tool: 'google-sheets'
}, {
  id: 24,
  text: 'Translating support docs',
  tool: 'zendesk'
}, {
  id: 25,
  text: 'Auditing cloud spend',
  tool: 'aws'
}, {
  id: 26,
  text: 'Creating onboarding checklist',
  tool: 'asana'
}, {
  id: 27,
  text: 'Syncing calendar events',
  tool: 'google-calendar'
}, {
  id: 28,
  text: 'Drafting email campaign',
  tool: 'mailchimp'
}, {
  id: 29,
  text: 'Updating CRM contacts',
  tool: 'hubspot'
}, {
  id: 30,
  text: 'Resolving merge conflicts',
  tool: 'gitlab'
}, {
  id: 31,
  text: 'Generating invoice PDFs',
  tool: 'stripe'
}, {
  id: 32,
  text: 'Monitoring server uptime',
  tool: 'datadog'
}, {
  id: 33,
  text: 'Sorting user feedback',
  tool: 'intercom'
}, {
  id: 34,
  text: 'Scheduling social posts',
  tool: 'twitter'
}, {
  id: 35,
  text: 'Reviewing pull requests',
  tool: 'github'
}, {
  id: 36,
  text: 'Updating project timeline',
  tool: 'asana'
}, {
  id: 37,
  text: 'Analyzing A/B test results',
  tool: 'datadog'
}, {
  id: 38,
  text: 'Drafting legal contract',
  tool: 'docusign'
}, {
  id: 39,
  text: 'Managing inventory levels',
  tool: 'shopify'
}, {
  id: 40,
  text: 'Tracking employee hours',
  tool: 'stripe'
}, {
  id: 41,
  text: 'Creating presentation slides',
  tool: 'figma'
}, {
  id: 42,
  text: 'Monitoring brand mentions',
  tool: 'twitter'
}, {
  id: 43,
  text: 'Updating knowledge base',
  tool: 'confluence'
}, {
  id: 44,
  text: 'Processing refunds',
  tool: 'stripe'
}, {
  id: 45,
  text: 'Assigning support tickets',
  tool: 'intercom'
}, {
  id: 46,
  text: 'Generating sales quotes',
  tool: 'salesforce'
}, {
  id: 47,
  text: 'Reviewing code quality',
  tool: 'github'
}, {
  id: 48,
  text: 'Managing email lists',
  tool: 'sendgrid'
}, {
  id: 49,
  text: 'Tracking bug reports',
  tool: 'jira'
}, {
  id: 50,
  text: 'Updating user permissions',
  tool: 'okta'
}];
type ToolLogo = {
  dashboardSlug: string;
  simpleIconSlug: string;
  monogram: string;
};

// Dashboard Icons and Simple Icons do not always use the same slug. Keeping
// those aliases explicit prevents a valid logo on one CDN from becoming a
// broken image when the execution feed rotates to that task.
const TOOL_LOGOS: Record<string, ToolLogo> = {
  notion: {
    dashboardSlug: 'notion',
    simpleIconSlug: 'notion',
    monogram: 'N'
  },
  salesforce: {
    dashboardSlug: 'salesforce',
    simpleIconSlug: 'salesforce',
    monogram: 'S'
  },
  linear: {
    dashboardSlug: 'linear',
    simpleIconSlug: 'linear',
    monogram: 'L'
  },
  mailchimp: {
    dashboardSlug: 'mailchimp',
    simpleIconSlug: 'mailchimp',
    monogram: 'M'
  },
  stripe: {
    dashboardSlug: 'stripe',
    simpleIconSlug: 'stripe',
    monogram: 'S'
  },
  zendesk: {
    dashboardSlug: 'zendesk',
    simpleIconSlug: 'zendesk',
    monogram: 'Z'
  },
  github: {
    dashboardSlug: 'github',
    simpleIconSlug: 'github',
    monogram: 'G'
  },
  hubspot: {
    dashboardSlug: 'hubspot',
    simpleIconSlug: 'hubspot',
    monogram: 'H'
  },
  slack: {
    dashboardSlug: 'slack',
    simpleIconSlug: 'slack',
    monogram: 'S'
  },
  jira: {
    dashboardSlug: 'jira',
    simpleIconSlug: 'jira',
    monogram: 'J'
  },
  figma: {
    dashboardSlug: 'figma',
    simpleIconSlug: 'figma',
    monogram: 'F'
  },
  quickbooks: {
    dashboardSlug: 'quickbooks',
    simpleIconSlug: 'quickbooks',
    monogram: 'Q'
  },
  asana: {
    dashboardSlug: 'asana',
    simpleIconSlug: 'asana',
    monogram: 'A'
  },
  wordpress: {
    dashboardSlug: 'wordpress',
    simpleIconSlug: 'wordpress',
    monogram: 'W'
  },
  'google-sheets': {
    dashboardSlug: 'google-sheets',
    simpleIconSlug: 'googlesheets',
    monogram: 'G'
  },
  'google-calendar': {
    dashboardSlug: 'google-calendar',
    simpleIconSlug: 'googlecalendar',
    monogram: 'G'
  },
  gitlab: {
    dashboardSlug: 'gitlab',
    simpleIconSlug: 'gitlab',
    monogram: 'G'
  },
  datadog: {
    dashboardSlug: 'datadog',
    simpleIconSlug: 'datadog',
    monogram: 'D'
  },
  intercom: {
    dashboardSlug: 'intercom',
    simpleIconSlug: 'intercom',
    monogram: 'I'
  },
  twitter: {
    dashboardSlug: 'twitter',
    simpleIconSlug: 'x',
    monogram: 'X'
  },
  docusign: {
    dashboardSlug: 'docusign',
    simpleIconSlug: 'docusign',
    monogram: 'D'
  },
  shopify: {
    dashboardSlug: 'shopify',
    simpleIconSlug: 'shopify',
    monogram: 'S'
  },
  confluence: {
    dashboardSlug: 'confluence',
    simpleIconSlug: 'confluence',
    monogram: 'C'
  },
  sendgrid: {
    dashboardSlug: 'sendgrid',
    simpleIconSlug: 'sendgrid',
    monogram: 'S'
  },
  okta: {
    dashboardSlug: 'okta',
    simpleIconSlug: 'okta',
    monogram: 'O'
  },
  aws: {
    dashboardSlug: 'aws',
    simpleIconSlug: 'amazonaws',
    monogram: 'A'
  }
};

// Resilient tool icon: the exact provider slugs above are tried across two
// independent CDNs, then a branded monogram preserves the card's balance if a
// provider ever changes an asset. A reset on tool change ensures a previous
// failed source never carries over to the next animated task.
function ToolIcon({
  tool,
  isLight
}: {
  tool: string;
  isLight?: boolean;
}) {
  const [stage, setStage] = useState(0);
  const logo = TOOL_LOGOS[tool] ?? {
    dashboardSlug: tool,
    simpleIconSlug: tool.replace(/-/g, ''),
    monogram: tool.charAt(0).toUpperCase()
  };
  const sources = [`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${logo.dashboardSlug}.svg`, `https://cdn.simpleicons.org/${logo.simpleIconSlug}/${isLight ? '2B211A' : 'ffffff'}`];
  useEffect(() => setStage(0), [tool]);
  if (stage >= sources.length) {
    return <span className={`text-[10px] md:text-xs font-bold leading-none ${isLight ? 'text-[#EA580C]' : 'text-white/80'}`} aria-hidden="true" data-id="element-5289" data-mp="5nl9bixp1t1q">
        
        {logo.monogram}
      </span>;
  }
  return <img key={`${tool}-${stage}`} src={sources[stage]} alt="" className={`w-4 h-4 md:w-5 md:h-5 object-contain ${isLight ? '' : 'opacity-90'}`} loading="eager" onError={() => setStage(current => current + 1)} data-id="element-5290" data-mp="ff7ht7inde0s" />;
}
// A single notification card. `isNewest` controls the status indicator:
// the freshest task (at the bottom of the feed) shows a spinner; once a newer
// task arrives below it, it flips to a completed checkmark and rises up.
function TaskCard({
  task,
  isNewest,
  zIndex,
  isLight = false
}: {
  task: (typeof TASKS)[0];
  isNewest: boolean;
  zIndex: number;
  isLight?: boolean;
}) {
  return <motion.div layout initial={{
    opacity: 0,
    y: 56,
    scale: 0.95
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -24,
    scale: 0.95
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 28
  }} style={{
    zIndex
  }} className={`relative rounded-xl p-3 md:p-4 flex items-center gap-3 md:gap-4 ${isLight ? 'bg-[#FFF7ED] border border-[#F8E2CC] shadow-sm' : 'bg-[#4A3B30] border border-white/15 shadow-xl'}`} data-id="element-5291" data-mp="24w31z1r7xrrz">
      
      <div className={`w-6 h-6 md:w-8 md:h-8 rounded-lg flex items-center justify-center shrink-0 ${isLight ? 'bg-white border border-[#F3E4D5]' : 'bg-white/10 border border-white/10'}`} data-id="element-5292" data-mp="2iu9qvse6whfi">
        
        <ToolIcon tool={task.tool} isLight={isLight} data-id="element-5293" data-mp="3biyjf316jefn" />
      </div>

      <div className="flex-1 min-w-0" data-id="element-5294" data-mp="4gpahoehd1gq">
        <div className={`text-xs md:text-sm font-medium truncate transition-colors duration-500 ${isNewest ? isLight ? 'text-[#2B211A]' : 'text-white' : isLight ? 'text-neutral-400' : 'text-[#B5A99D]'}`} data-id="element-5295" data-mp="4hqmpw78fu01">
          
          {task.text}
        </div>
        <div className={`text-[10px] md:text-[11px] mt-0.5 ${isLight ? 'text-neutral-400' : 'text-white/40'}`} data-id="element-5296" data-mp="9gbldr6u65md">
          
          Autonomous execution
        </div>
      </div>

      <div className="shrink-0 relative w-4 h-4 md:w-5 md:h-5 flex items-center justify-center" data-id="element-5297" data-mp="ndgmdcrp0ear">
        {isNewest ? <motion.div className="absolute inset-0 text-[#EA580C]" animate={{
        rotate: 360
      }} transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-5298" data-mp="x5mo3e7qya35">
          
            <Loader2 className="w-4 h-4 md:w-5 md:h-5" data-id="element-5299" data-mp="1661ayi0czx8r" />
          </motion.div> : <motion.div className="absolute inset-0 flex items-center justify-center" initial={{
        opacity: 0,
        scale: 0.4
      }} animate={{
        opacity: [0, 1, 1],
        scale: [0.4, 1.35, 1]
      }} transition={{
        duration: 0.5,
        times: [0, 0.55, 1],
        ease: [0.34, 1.56, 0.64, 1]
      }} data-id="element-5300" data-mp="16480ssqjeuw0">
          
            <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-[#EA580C] flex items-center justify-center" data-id="element-5301" data-mp="1bt4jflzf5scw">
              <Check className="w-2.5 h-2.5 md:w-3 md:h-3 text-white" strokeWidth={3} data-id="element-5302" data-mp="1gqd2dose60f7" />
            
            </div>
          </motion.div>}
      </div>
    </motion.div>;
}
// How many cards are visible in the stack at once. A fuller stack reads as
// high-volume / "lots happening at once".
const VISIBLE_COUNT = 4;
type StackItem = {
  key: number;
  task: (typeof TASKS)[0];
};
export function SignupShowcase({
  variant = 'dark',
  counterPosition = 'top',
  compactCounter = false,
  headlinePosition = 'top'
}: {
  variant?: 'dark' | 'light';
  counterPosition?: 'top' | 'bottom';
  compactCounter?: boolean;
  headlinePosition?: 'top' | 'bottom';
}) {
  const isLight = variant === 'light';
  const [count, setCount] = useState(14208);
  const reduceMotion = useReducedMotion();
  // Notification stack: newest item is always last (rendered at the bottom,
  // solid), older items rise up and the oldest exits at the top.
  const [stack, setStack] = useState<StackItem[]>(() => TASKS.slice(0, VISIBLE_COUNT).map((task, i) => ({
    key: i,
    task
  })));
  const nextRef = useRef(VISIBLE_COUNT);
  // Push a fresh task into the bottom of the feed. We pick a random task whose
  // tool isn't already showing in the visible stack, so no two cards on screen
  // ever share the same icon (which read as a glitchy repeat). Unique keys let
  // several cards animate in at once without collisions.
  const pushTask = useCallback(() => {
    setStack(prev => {
      const n = nextRef.current;
      nextRef.current = n + 1;
      const visibleTools = new Set(prev.map(item => item.task.tool));
      const candidates = TASKS.filter(t => !visibleTools.has(t.tool));
      const pool = candidates.length > 0 ? candidates : TASKS;
      const newItem: StackItem = {
        key: n,
        task: pool[Math.floor(Math.random() * pool.length)]
      };
      return [...prev, newItem].slice(-VISIBLE_COUNT);
    });
    // Increment the counter exactly when a new task is pushed to the feed.
    // This perfectly syncs the number ticking up with the visual of a task
    // "completing" (flipping to a checkmark as it rises).
    setCount(c => c + 1);
  }, []);
  useEffect(() => {
    if (reduceMotion) return;
    let timeout: ReturnType<typeof setTimeout>;
    // Self-scheduling loop with irregular gaps — and an occasional rapid
    // double-drop — so the feed feels live and busy rather than metronomic.
    const tick = () => {
      pushTask();
      // ~1-in-4 ticks fire a second card right behind the first, creating an
      // overlapping burst.
      if (Math.random() < 0.25) {
        setTimeout(pushTask, 140 + Math.random() * 120);
      }
      const nextGap = 420 + Math.random() * 760; // 420–1180ms
      timeout = setTimeout(tick, nextGap);
    };
    timeout = setTimeout(tick, 600);
    return () => clearTimeout(timeout);
  }, [reduceMotion, pushTask]);
  const liveCounter = compactCounter ?
  // Compact, subtle counter — sits below the notification animation as a
  // supporting caption rather than a hero stat.
  <div className={`mt-3 flex items-center justify-center gap-2 relative z-20 ${counterPosition === 'top' ? 'mb-6 md:mb-10 mt-0' : ''}`} data-id="element-5303" data-mp="26y9760wk5tyb">
    
      <span className={`text-[10px] md:text-[11px] font-mono font-medium uppercase tracking-[0.18em] ${isLight ? 'text-neutral-400' : 'text-white/40'}`} data-id="element-5304" data-mp="1f9z31394kauu">
      
        Tasks completed
      </span>
      <span className={`font-mono text-sm md:text-base font-semibold tracking-tight ${isLight ? 'text-neutral-500' : 'text-white/60'}`} data-id="element-5305" data-mp="1o9m4tq5rniof">
      
        <RollingCounter value={count} data-id="element-5306" data-mp="10ignf6k6gic0" />
      </span>
    </div> : <div className={`flex flex-col items-center gap-1.5 relative z-20 ${counterPosition === 'top' ? 'mb-6 md:mb-10' : 'mt-6 md:mt-8'}`} data-id="element-5307" data-mp="vlym6go2324f">
    
      <div className={`text-[11px] md:text-xs font-mono font-semibold uppercase tracking-[0.2em] ${isLight ? 'text-neutral-500' : 'text-white/50'}`} data-id="element-5308" data-mp="376fnx27qm51c">
      
        Tasks completed
      </div>
      <div className="relative flex items-center gap-3" data-id="element-5309" data-mp="2y9ovr0wb3ac1">
        <span className="font-mono text-3xl md:text-4xl font-bold tracking-tight text-[#F97316]" style={{
        textShadow: '0 0 28px rgba(234,88,12,0.35)'
      }} data-id="element-5310" data-mp="3365lu0n2fyrr">
        
          <RollingCounter value={count} data-id="element-5311" data-mp="3uxutr4yq34hc" />
        </span>
      </div>
    </div>;

  // Bold headline (hidden on mobile). Can lead the pane (top) or sit beneath
  // the live feed (bottom) so the animation aligns with the auth headline on
  // the right.
  const headlineBlock = <div className={`hidden md:block w-full relative z-20 ${headlinePosition === 'bottom' ? 'mt-8 md:mt-10 text-center' : 'mb-6 text-left'}`} data-id="element-5312" data-mp="3lzj69bt5kmt4">
    
      <h2 className="font-title font-medium text-4xl md:text-5xl tracking-tight text-white leading-[1.1]" data-id="element-5313" data-mp="2aijgamua2k29">
        Your new
        <br data-id="element-5314" data-mp="1qfna3splhxq0" />
        <span className="text-[#F97316]" data-id="element-5315" data-mp="1qfdglv5cd8fk">force multiplier.</span>
      </h2>
    </div>;
  return <div className="w-full max-w-md flex flex-col items-center" data-id="element-5316" data-mp="1mf3dqrcuiy4x">
      {headlinePosition === 'top' && headlineBlock}

      {counterPosition === 'top' && liveCounter}

      {/* The Execution Pipeline */}
      <div className={`relative w-full h-[200px] md:h-[320px] rounded-3xl overflow-hidden flex items-end justify-center ${isLight ? '' : 'bg-[#1C1510] border border-white/5 shadow-2xl'}`} data-id="element-5317" data-mp="1coi7qiuuerbz">
        
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#EA580C]/10 blur-[80px] rounded-full" data-id="element-5318" data-mp="12p9h9zz6ruud" />

        {/* Notification stack: newest always enters at the bottom, fully solid;
         older cards rise up and fade out at the top. */}
        <div className="relative w-full max-w-[280px] md:max-w-[300px] z-20 pb-6 md:pb-10 flex flex-col gap-2 md:gap-3 px-4 md:px-0" data-id="element-5319" data-mp="2t1p6d8di0joh">
          <AnimatePresence initial={false} data-id="element-5320" data-mp="2o1uhtbh7466l">
            {stack.map((item, i) => <TaskCard key={item.key} task={item.task} isNewest={i === stack.length - 1} zIndex={i + 1} isLight={isLight} data-id="element-5321" data-mp="2vmn1meooflpi" data-mpi={i} />)}
          </AnimatePresence>
        </div>

        {/* Top fade so the oldest card dissolves out of the stage */}
        <div className={`absolute inset-x-0 top-0 h-16 md:h-20 bg-gradient-to-b to-transparent z-30 pointer-events-none ${isLight ? 'from-[#FAF8F4]' : 'from-[#1C1510]'}`} data-id="element-5322" data-mp="35flvoc2y04gi" />
        
      </div>

      {counterPosition === 'bottom' && liveCounter}

      {headlinePosition === 'bottom' && headlineBlock}
    </div>;
}